import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authFetch, httpClient } from "../HttpClient";
import { fetchClientList } from "./Portefeuille/useClients.js";

// Règles d'accès cabinet
// null = pas de filtre (accès à tous)
const GLOBAL_ACCESS_ROLES = ["SOCIAL", "JURIDIQUE"];

export const getCabinetIdFilter = (utilisateur) => {
  if (!utilisateur) return null;
  if (utilisateur.allCabinetsAccess) return null; // fondateurs + malgaches
  if (GLOBAL_ACCESS_ROLES.includes(utilisateur.role)) return null; // social + juridique
  return utilisateur.userCabinets?.[0]?.cabinetId ?? null;
};

// Portefeuille lecture = visible par tous — cette fonction concerne les écritures uniquement
export const canEditClient = (utilisateur, clientCabinetId) => {
  if (!utilisateur) return false;
  if (utilisateur.allCabinetsAccess) return true;
  if (GLOBAL_ACCESS_ROLES.includes(utilisateur.role)) return true;
  if (!clientCabinetId) return true;
  return (
    utilisateur.userCabinets?.some((uc) => uc.cabinetId === clientCabinetId) ??
    false
  );
};

// Identité juridique des cabinets — alimente le sélecteur "Sélection du
// cabinet" des formulaires LM : la ville va sur la page de garde
// (lieu_collab) et les 7 champs remplissent le pied de page juridique des
// Word via docxtemplater (tags : denomination_cab, forme_cab, capital_cab,
// siege_cab, siren_cab, siret_cab, ville_rcs_cab).
export const CABINETS_LM = {
  "LE TAMPON": {
    denomination_cab: "ALFA COMPTABILITE AUDIT",
    forme_cab: "SAS",
    capital_cab: "30 000 €",
    siege_cab: "27 rue Vallon Hoarau, LE TAMPON 97430",
    siren_cab: "832 403 273",
    siret_cab: "832 403 273 00036",
    ville_rcs_cab: "Saint-Pierre De La Réunion",
  },
  "SAINT-PIERRE": {
    denomination_cab: "ALFA 4.10",
    forme_cab: "SAS",
    capital_cab: "100 000 €",
    siege_cab: "19 chemin du Château d'Eau, Local 23, SAINT-PIERRE 97410",
    siren_cab: "105 454 334",
    siret_cab: "105 454 334 00019",
    ville_rcs_cab: "Saint-Pierre De La Réunion",
  },
};

export const EtatGlobalContext = createContext();

export const EtatGlobalProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Chargement propre aux LM (colonnes Proposition LM / LM signée du Lead) :
  // `isLoading` est partagé et repasse à false dès qu'un autre appel finit,
  // d'où un « Aucun lead trouvé » affiché avant l'arrivée des LM. Vrai
  // uniquement au premier chargement : un rafraîchissement ne remet pas le
  // skeleton.
  const [loadingDocumentsLM, setLoadingDocumentsLM] = useState(true);
  const [utilisateur, setUtilisateur] = useState(() => {
    const savedUser = localStorage.getItem("utilisateur");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // Session admin mise de côté pendant une usurpation ("Se connecter en tant
  // que") — survit à un refresh de page tant qu'on n'est pas revenu dessus.
  const [impersonatorSession, setImpersonatorSession] = useState(() => {
    const saved = localStorage.getItem("impersonatorSession");
    return saved ? JSON.parse(saved) : null;
  });

  const CURRENT_UPDATE_VERSION = 11;

  const savedVersion = Number(localStorage.getItem("update_version")) || 0;

  const [showUpdateNotification, setShowUpdateNotification] = React.useState(
    savedVersion !== CURRENT_UPDATE_VERSION
  );

  const closeUpdateNotification = () => {
    localStorage.setItem("update_version", CURRENT_UPDATE_VERSION);
    setShowUpdateNotification(false);
  };

  const resetUpdateNotification = () => {
    localStorage.setItem("update_version", 0);
    setShowUpdateNotification(true);
  };

  // Précharge le cache React Query ['clients'] au login (staleTime: Infinity
  // côté useClients — voir Portefeuille/useClients.js) : plusieurs écrans
  // (recherche client dans SignModif notamment) dépendent d'un filtrage
  // instantané en mémoire, sans aller-retour réseau à chaque frappe.
  const fetchClietns = () =>
    queryClient.prefetchQuery({
      queryKey: ["clients"],
      queryFn: fetchClientList,
      staleTime: Infinity,
    });

  const fetchDocumentsLM = () => {
    setIsLoading(true);
    httpClient
      .get("https://backend-myalfa.vercel.app/api/all-documents")
      .then((response) => {
        setDocuments(response.data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des documents", error);
        setIsLoading(false);
      })
      .finally(() => setLoadingDocumentsLM(false));
  };

  const fetchUsers = () => {
    setIsLoading(true);
    httpClient
      .get("https://backend-myalfa.vercel.app/api/users")
      .then((response) => {
        const sortedUsers = response.data.sort((a, b) => a.id - b.id);
        setUsers(sortedUsers);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des utilisateurs", error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (utilisateur) {
      localStorage.setItem("utilisateur", JSON.stringify(utilisateur));
    } else {
      localStorage.removeItem("utilisateur");
    }
  }, [utilisateur]);

  // "Se connecter en tant que" — met de côté la session admin actuelle puis
  // adopte le token/user renvoyés par POST /users/:id/impersonate (gate
  // allCabinetsAccess côté backend). ["clients"] a un staleTime: Infinity
  // (voir fetchClietns) : sans invalidation explicite, fetchAll() ne le
  // rafraîchirait jamais tout seul au changement d'identité.
  const impersonateUser = async (userId) => {
    const { data } = await httpClient.post(
      `https://backend-myalfa.vercel.app/api/users/${userId}/impersonate`
    );

    const adminSession = {
      token: localStorage.getItem("token"),
      user: JSON.parse(localStorage.getItem("user") || "null"),
    };
    localStorage.setItem("impersonatorSession", JSON.stringify(adminSession));
    setImpersonatorSession(adminSession);

    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUtilisateur(data.user);

    queryClient.invalidateQueries({ queryKey: ["clients"] });
  };

  const stopImpersonation = () => {
    if (!impersonatorSession) return;

    localStorage.setItem("token", impersonatorSession.token);
    localStorage.setItem("user", JSON.stringify(impersonatorSession.user));
    setUtilisateur(impersonatorSession.user);

    localStorage.removeItem("impersonatorSession");
    setImpersonatorSession(null);

    queryClient.invalidateQueries({ queryKey: ["clients"] });
  };

  const [formData, setFormData] = useState({
    // LM Logiciel
    tarif: "CLASSIQUE",
    details_tarif: `Mon Expert en Gestion (MEG) – Full Web – 1 Utilisateur -
    Modules :
    -      DEVIS / FACTURATION
    Sans engagement`,
    details_tarif_prix: "15,00",
    details_tarif_prix_tva: "1,27",
    details_tarif_prix_total: "16,25",
    //
    diligences: " ",
    honoraires: " ",
    abrv: " ",
    selectedForm: " ",
    forme_sociale: "EI",
    nom_client: " ",
    date_jour: (() => {
      const date = new Date();
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    })(),
    mission_proposer: "Tenue",
    lieu_collab: " ",
    // Identité juridique du cabinet sélectionné (pied de page des LM)
    denomination_cab: " ",
    forme_cab: " ",
    capital_cab: " ",
    siege_cab: " ",
    siren_cab: " ",
    siret_cab: " ",
    ville_rcs_cab: " ",
    civilite_lm: "Monsieur",
    civilite_sign: "Le client",
    nom: " ",
    prenom: " ",
    email: " ",
    telephone: " ",
    adresse: " ",
    siren: " ",
    siret: " ",
    acticite_principale: " ",
    date_cloture_jour: "31",
    date_cloture_mois: "12",
    date_cloture: "31/12",
    effectif: "-",
    organisation_comptable: "Sous-traitance",
    volumes_annuels: " ",
    mission_comptables: "☐",
    mission_sociale: "☐",
    mission_juridique: "☐",
    date_creation: " ",
    date_debut: " ",
    date_fin: " ",
    premiere_dsn_raw: " ",
    premiere_dsn: " ",
    premier_tri_dsn: " ",
    premier_tri_dsn_raw: " ",
    expert_comptable: "Samuel HOARAU",
    expert_comptable_nom: "Samuel HOARAU",
    hono_compta: "42,50",
    hono_compta_bnc: "227,85",
    hono_compta_me: "99,90",
    hono_juridique: "42,50",
    hono_social: "30,00",
    hono_tele: "110,00 €",
    mission_complementaire: "- NEANT",
    mois_intervention: " ",
    total_honoraires: " ",
    TVA_honoraires_pourcentage: "8,50",
    TVA_honoraires: " ",
    total_honoraires_ttc: " ",
    type_evaluation: "vos droits sociaux",
    details_type_evaluation: " ",
    champ_libre: " ",
    texteFinal:
      "Cette lettre reste en vigueur pour les exercices futurs, sauf en cas de résiliation, de modification ou d'annulation de notre mission selon les modalités décrites dans les conditions générales et financières jointes.",
    declaration_ir: "☐",
    declaration_tns: "☐",
    declaration_ds: "☐",
    declaration_ifi: "☐",
    pr_annee: " ",
    hono_ir: "350,00",
    hono_ir_12: "29,17",
    avenant: "☐",
    numero_avenant: " ",
    date_avenant: " ",
    textAvenant: " ",
    annPre: "☐",
    textAnnPre: "3",
    // tableau annexe 2
    odlc_pa2: ["M"],
    odlc_cac_a2: "X",
    odlc_clc_a2: "X",
    odlc_aic_a2: " ",
    //
    tdf_pa2: ["A"],
    tdf_cac_a2: "X",
    tdf_clc_a2: " ",
    tdf_aic_a2: " ",
    //
    sip_pa2: ["A"],
    svdp_pa2: ["A"],
    sip_cac_a2: " ",
    svdp_cac_a2: " ",
    sip_clc_a2: "X",
    svdp_clc_a2: "X",
    sip_aic_a2: " ",
    svdp_aic_a2: " ",
    //
    jdc_pa2: ["A"],
    jdc_cac_a2: "X",
    jdc_clc_a2: "X",
    jdc_aic_a2: " ",
    //
    cdt_pa2: ["A"],
    cdt_cac_a2: "X",
    cdt_clc_a2: " ",
    cdt_aic_a2: " ",
    //
    cedc_pa2: ["A"],
    cedc_cac_a2: "X",
    cedc_clc_a2: " ",
    cedc_aic_a2: " ",
    //
    cps_pa2: ["A"],
    cps_cac_a2: "X",
    cps_clc_a2: " ",
    cps_aic_a2: " ",
    //
    jds_pa2: ["A"],
    jds_cac_a2: "X",
    jds_clc_a2: " ",
    jds_aic_a2: " ",
    //
    ecdc_pa2: ["A"],
    ecdc_cac_a2: "X",
    ecdc_clc_a2: " ",
    ecdc_aic_a2: " ",
    //
    edca_pa2: ["A"],
    edca_cac_a2: "X",
    edca_clc_a2: " ",
    edca_aic_a2: " ",
    //
    tdrl_pa2: ["A"],
    tdrl_cac_a2: "X",
    tdrl_clc_a2: " ",
    tdrl_aic_a2: " ",
    //
    dce_pa2: ["A"],
    dca_pa2: ["A"],
    dce_cac_a2: "X",
    dca_cac_a2: " ",
    dce_clc_a2: " ",
    dca_clc_a2: "X",
    dce_aic_a2: " ",
    dca_aic_a2: " ",
    //
    eem_pa2: ["P"],
    eem_cac_a2: "X",
    eem_clc_a2: " ",
    eem_aic_a2: " ",
    //
    cdp_pa2: ["M"],
    cdp_cac_a2: "X",
    cdp_clc_a2: " ",
    cdp_aic_a2: " ",
    //
    edlc_pa2: ["M", "T", "A"],
    edlco_pa2: [],
    edlc_cac_a2: " ",
    edlco_cac_a2: "X",
    edlc_clc_a2: " ",
    edlco_clc_a2: " ",
    edlc_aic_a2: " ",
    edlco_aic_a2: " ",
    //
    ededr_pa2: ["M", "T", "A"],
    ededr_cac_a2: "X",
    ededr_clc_a2: " ",
    ededr_aic_a2: " ",
    //
    ctdd_pa2: ["J"],
    csdo_pa2: [],
    ctdd_cac_a2: " ",
    csdo_cac_a2: " ",
    ctdd_clc_a2: "X",
    csdo_clc_a2: "X",
    ctdd_aic_a2: " ",
    csdo_aic_a2: " ",
    //
    adf_pa2: ["A"],
    adf_cac_a2: "X",
    adf_clc_a2: " ",
    adf_aic_a2: " ",
    //
    cdfdc_pa2: ["A"],
    cdfdc_cac_a2: "X",
    cdfdc_clc_a2: " ",
    cdfdc_aic_a2: " ",
    //
    cdfde_pa2: ["A"],
    cdfde_cac_a2: "X",
    cdfde_clc_a2: " ",
    cdfde_aic_a2: " ",
    //
    adfec_pa2: ["A"],
    adfec_cac_a2: "X",
    adfec_clc_a2: " ",
    adfec_aic_a2: " ",
    //
    eddr_pa2: ["A"],
    eddr_cac_a2: "X",
    eddr_clc_a2: " ",
    eddr_aic_a2: " ",
    //
    edcoa_pa2: ["A"],
    edcoa_cac_a2: "X",
    edcoa_clc_a2: " ",
    edcoa_aic_a2: " ",
    //
    edddt_pa2: ["ND", "A"],
    edddt_cac_a2: "X",
    edddt_clc_a2: " ",
    edddt_aic_a2: " ",
    //
    eddddb_pa2: ["A"],
    eddddb_cac_a2: " ",
    eddddb_clc_a2: "X",
    eddddb_aic_a2: " ",
    //
    dlas_pa2: ["ND", "A"],
    dlas_cac_a2: "X",
    dlas_clc_a2: " ",
    dlas_aic_a2: " ",
    tsrlv_pa2: ["A"],
    tsrlv_cac_a2: "X",
    tsrlv_clc_a2: " ",
    tsrlv_aic_a2: " ",
    ddcdp_pa2: ["A"],
    ddcdp_cac_a2: " ",
    ddcdp_clc_a2: "X",
    ddcdp_aic_a2: " ",
    das_pa2: ["A"],
    das_cac_a2: "X",
    das_clc_a2: " ",
    das_aic_a2: " ",
    //
    cfe_pa2: ["A"],
    cfe_cac_a2: "X",
    cfe_clc_a2: " ",
    cfe_aic_a2: " ",
    //
    cvae_pa2: ["A"],
    cvae_cac_a2: "X",
    cvae_clc_a2: " ",
    cvae_aic_a2: " ",
    //
    is_pa2: ["ND", "A"],
    is_cac_a2: "X",
    is_clc_a2: " ",
    is_aic_a2: " ",
    //
    //
    // tableau annexe 3
    cet_pa3: ["D"],
    cet_ec_a3: " ",
    cet_c_a3: "X",
    //
    cnil_pa3: ["P"],
    cnil_ec_a3: " ",
    cnil_c_a3: "X",
    //
    pdc_pa3: ["D"],
    pdc_ec_a3: "X",
    pdc_c_a3: " ",
    //
    cid_pa3: ["D"],
    cid_ec_a3: "X",
    cid_c_a3: " ",
    //
    pdr_pa3: ["D"],
    pdr_ec_a3: "X",
    pdr_c_a3: " ",
    //
    rdb_pa3: ["D"],
    rdb_ec_a3: "X",
    rdb_c_a3: " ",
    //
    cetp_pa3: ["M"],
    cetp_ec_a3: " ",
    cetp_c_a3: "X",
    //
    edb_pa3: ["M"],
    edb_ec_a3: "X",
    edb_c_a3: " ",
    //
    edr_pa3: ["M"],
    edr_ec_a3: " ",
    edr_c_a3: "X",
    //
    cdr_pa3: ["M"],
    cdr_ec_a3: " ",
    cdr_c_a3: "X",
    //
    pdl_pa3: ["M"],
    pdl_ec_a3: " ",
    pdl_c_a3: "X",
    //
    edl_pa3: ["M"],
    edl_ec_a3: "X",
    edl_c_a3: " ",
    //
    pde_pa3: ["M"],
    pde_ec_a3: "X",
    pde_c_a3: " ",
    //
    sid_pa3: ["M"],
    sid_ec_a3: "X",
    sid_c_a3: " ",
    //
    pdls_pa3: ["P"],
    pdls_ec_a3: "X",
    pdls_c_a3: " ",
    //
    pdp_pa3: ["P"],
    pdp_ec_a3: "X",
    pdp_c_a3: " ",
    //
    dsn_pa3: ["M"],
    dsn_ec_a3: "X",
    dsn_c_a3: " ",
    //
    dac_pa3: ["M", "T"],
    dac_ec_a3: "X",
    dac_c_a3: " ",
    //
    dao_pa3: ["M", "T"],
    dao_ec_a3: "X",
    dao_c_a3: " ",
    //
    dal_pa3: ["M", "T"],
    dal_ec_a3: "X",
    dal_c_a3: " ",
    //
    dalm_pa3: ["M", "T"],
    dalm_ec_a3: "X",
    dalm_c_a3: " ",
    //
    daf_pa3: ["A"],
    daf_ec_a3: "X",
    daf_c_a3: " ",
    //
    dads_pa3: ["A"],
    dads_ec_a3: "X",
    dads_c_a3: " ",
    //
    tdd_pa3: ["M", "T", "A"],
    tdd_ec_a3: "X",
    tdd_c_a3: " ",
    //
    ras_pa3: ["P"],
    ras_ec_a3: " ",
    ras_c_a3: "X",
    //
    tddi_pa3: ["M"],
    tddi_ec_a3: "X",
    tddi_c_a3: " ",
    //
    tdru_pa3: ["P"],
    tdru_ec_a3: " ",
    tdru_c_a3: "X",
    //
    tddd_pa3: ["P", "M", "A"],
    tddd_ec_a3: " ",
    tddd_c_a3: "X",
    //
    eema_pa3: ["P", "A"],
    eema_ec_a3: " ",
    eema_c_a3: "X",
    //
    tdrd_pa3: ["P"],
    tdrd_ec_a3: " ",
    tdrd_c_a3: "X",
    //
    tdrm_pa3: ["P"],
    tdrm_ec_a3: " ",
    tdrm_c_a3: "X",
    //
    tdrde_pa3: ["P"],
    tdrde_ec_a3: " ",
    tdrde_c_a3: "X",
    //
    eetd_pa3: ["A"],
    eetd_ec_a3: " ",
    eetd_c_a3: "X",
    //
    mep_pa3: ["A"],
    mep_ec_a3: " ",
    mep_c_a3: "X",
    //
    mepe_pa3: ["P"],
    mepe_ec_a3: " ",
    mepe_c_a3: "X",
    //
    edld_pa3: ["P"],
    edld_ec_a3: " ",
    edld_c_a3: "X",
    // Tabeleau BNC
    bdrcl: "X",
    bdro: " ",
    //
    jdbcl: " ",
    jdbca: "X",
    jdbo: " ",
    //
    jdccl: " ",
    jdcca: "X",
    jdco: " ",
    //
    jddcl: " ",
    jddca: "X",
    jddo: " ",
    //
    cdpjcl: " ",
    cdpjca: "X",
    cdpjo: " ",
    //
    rbcl: " ",
    rbca: "X",
    rbo: " ",
    //
    cjcl: " ",
    cjca: "X",
    cjo: " ",
    //
    bcl: " ",
    bca: "X",
    bo: " ",
    //
    glcl: " ",
    glca: "X",
    glo: " ",
    //
    ecdfdcl: " ",
    ecdfdca: "X",
    ecdfdo: " ",
    //
    rdicl: " ",
    rdica: "X",
    rdio: " ",
    //
    cdpjucl: "X",
    cdpjuca: " ",
    cdpjuo: " ",
    //
    cdicl: " ",
    cdica: "X",
    cdio: " ",
    //
    df2cl: " ",
    df2ca: "X",
    df2o: " ",
    //
    ddcdcl: " ",
    ddcdca: "X",
    ddcdo: " ",
    //
    dedscl: "X",
    dedsca: " ",
    dedso: " ",
    //
    ddhcl: " ",
    ddhca: "X",
    ddho: " ",
    //
    dfdapcl: " ",
    dfdapca: " ",
    dfdapo: " ",
    //
    ddlaacl: " ",
    ddlaaca: "X",
    ddlaao: " ",
    //
    aecdvfcl: " ",
    aecdvfca: " ",
    aecdvfo: "Sur devis",
    //
    bdpcl: " ",
    bdpca: "X",
    bdpo: " ",
    //
    dmotaoscl: " ",
    dmotaosca: "X",
    dmotaoso: " ",
    //
    rocl: " ",
    roca: "X",
    roo: " ",
    //
    dadsaoscl: " ",
    dadsaosca: "X",
    dadsaoso: " ",
    //
    daoscl: " ",
    daosca: "X",
    daoso: " ",
    //
    aacepldoscl: " ",
    aacepldosca: " ",
    aacepldoso: "Sur devis",
    //
    dgcl: " ",
    dgca: "X",
    dgo: " ",
    //
    tdbcl: " ",
    tdbca: " ",
    tdbo: "Sur devis",
    //
    bucl: " ",
    buca: " ",
    buo: "Sur devis",
    //
    dpcl: " ",
    dpca: " ",
    dpo: "Sur devis",
    //
    fdcdacl: " ",
    fdcdaca: " ",
    fdcdao: " ",
    //
    pvdacl: " ",
    pvdaca: " ",
    pvdao: " ",
    //
    rdgaacl: " ",
    rdgaaca: " ",
    rdgaao: " ",
    //
    fdpacl: " ",
    fdpaca: " ",
    fdpao: " ",
    //
    majdrocl: " ",
    majdroca: " ",
    majdroo: " ",
    // Tableu Création
    pdcdpcl: "X",
    pdcdpca: "X",
    pdcdpo: " ",
    //
    edps3cl: " ",
    edps3ca: " ",
    edps3o: "Sur devis",
    //
    rdfeaalcl: " ",
    rdfeaalca: " ",
    rdfeaalo: "Sur devis",
    //
    aacdscl: " ",
    aacdsca: "X",
    aacdso: " ",
    //
    rdsedcpcl: " ",
    rdsedcpca: "X",
    rdsedcpo: " ",
    //
    rddnedlmcl: "X",
    rddnedlmca: " ",
    rddnedlmo: "Sur devis",
    //
    aaldfdccl: " ",
    aaldfdcca: "X",
    aaldfdco: " ",
    //
    dpodaapcl: "X",
    dpodaapca: " ",
    dpodaapo: " ",
    //
    ddcalbcl: "X",
    ddcalbca: " ",
    ddcalbo: " ",
    //
    oaeccl: " ",
    oaecca: "X",
    oaeco: " ",
    //
    demcl: " ",
    demca: "X",
    demo: " ",
    //
    pedd: " ",
    pp: " ",
    pce: " ",
    //
    ///
    ///
    mecdo: "☐",
    mefdi: "☐",
    aalcdlme: "N/A",
    ///
    meaalm: "☐",
    mecalr: "☐",
    adlme: "N/A",
    ///
    mepvm: "☐",
    mepvmsu: "☐",
    mepnc: "☒",
    sc: "INCLUS DANS LE FORFAIT",
    ///
    medpvm1: "☐",
    medpvc1: "☒",
    aaddt: "INCLUS DANS LE FORFAIT",
    ///
    medpvm2: "☐",
    medpvc2: "☒",
    aaddcs: "INCLUS DANS LE FORFAIT",
    ///
    mepvmaa: "☒",
    meaplc: "☐",
    meedl: "☐",
    aaldds: "N/A",
    ///
    merddds: "☒",
    meaeac: "☒",
    sfa: "INCLUS DANS LE FORFAIT",
    //
    merdsc: "☐",
    meaeac2: "☐",
    aslcds: "SUR DEVIS",
    //
    meapvi: "☐",
    merdf: "☐",
    apvi: "SUR DEVIS",
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      textAvenant:
        prev.avenant === "☒"
          ? `AVENANT n°${prev.numero_avenant} à la lettre de mission du ${prev.date_avenant}`
          : " ",
    }));
  }, [
    formData.avenant,
    formData.numero_avenant,
    formData.date_avenant,
    formData.annPre,
    formData.textAvenant,
    formData.textAnnPre,
  ]);

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      date_cloture: `${prevFormData.date_cloture_jour}/${prevFormData.date_cloture_mois}`,
    }));
  }, [formData.date_cloture_jour, formData.date_cloture_mois]);

  const [rows, setRows] = useState([
    { diligence: "", honoraire: "", abrv: "", suggestions: [] },
  ]);

  // Fonction pour mettre à jour les rows
  const updateRows = (updatedRows) => {
    setRows(updatedRows);
    updateFormData(updatedRows);
  };
  const updateFormData = (updatedRows) => {
    const diligencesArray = updatedRows.map((row) => row.diligence);
    const abrvArray = updatedRows.map((row) => row.abrv);
    const honorairesArray = updatedRows.map((row) => row.honoraire);

    const MAX_LENGTH = 70; // seuil de longueur pour un saut de ligne

    let newHonorairesLines = [];

    for (let i = 0; i < honorairesArray.length; i++) {
      // Ajout de l'honoraire courant
      newHonorairesLines.push(honorairesArray[i]);

      if (i < honorairesArray.length - 1) {
        // On regarde uniquement la longueur de abrv
        const abrvLength = abrvArray[i].trim().length;

        // Nombre de sauts à ajouter
        const jumps =
          abrvLength > MAX_LENGTH ? Math.floor(abrvLength / MAX_LENGTH) : 0;

        // Ajout des lignes vides
        for (let j = 0; j < jumps; j++) {
          newHonorairesLines.push("");
        }
      }
    }

    const newHonoraires = newHonorairesLines.join("\r\n");
    const newDiligences = diligencesArray.join("\r\n");
    const newAbrv = abrvArray.join("\r\n");

    setFormData((prev) => ({
      ...prev,
      diligences: newDiligences,
      abrv: newAbrv,
      honoraires: newHonoraires,
    }));
  };

  useEffect(() => {
    const has5Ans = rows.some((row) =>
      row.abrv
        ?.toLowerCase()
        .includes("rapport financier prévisionnel sur 5 années")
    );

    const has3Ans = rows.some((row) =>
      row.abrv
        ?.toLowerCase()
        .includes("rapport financier prévisionnel sur 3 années")
    );

    let newValue = "3";

    // ✅ Règle métier :
    // - Si uniquement 5 ans → 5
    // - Si 3 ans OU les deux → 3
    if (has5Ans && !has3Ans) {
      newValue = "5";
    }

    setFormData((prev) => ({
      ...prev,
      textAnnPre: newValue,
    }));
  }, [rows, setFormData]);

  const [nombreLignesTableau, setNombreLignesTableau] = useState(0);
  // Compteur séparé de nombreLignesTableau : pour "LM Création de société",
  // ce dernier est déjà piloté par le tableau d'honoraires (LMCREATION/
  // HONOTableau.js écrase avec rows.length) — le texte libre a donc besoin
  // de son propre compteur pour décaler la signature sans écraser/être
  // écrasé par celui du tableau.
  const [nombreLignesTexteLibre, setNombreLignesTexteLibre] = useState(0);
  const [afficheLM, setAfficheLM] = useState(false);
  // Affichage "champ requis" du sélecteur de cabinet des LM : activé uniquement
  // après un clic sur "Enregistrer le brouillon" ou "Visualiser et soumettre"
  // alors qu'aucun cabinet n'est sélectionné (jamais à l'ouverture du formulaire).
  const [cabinetRequisVisible, setCabinetRequisVisible] = useState(false);
  const [leadIdGlobal, setLeadIdGlobal] = useState(null);
  // Même mécanisme que leadIdGlobal, pour "Création LM" depuis la fiche
  // client (FicheClientOverlay.js) : bascule vers SignModif via afficheLM
  // sans passer par une route, donc sans props à faire descendre.
  const [clientIdGlobal, setClientIdGlobal] = useState(null);
  // Brouillon de LM en cours d'édition ({ id, cle }) : retenu ici (et non dans
  // SignModif) car SignModif est démonté quand on passe à l'aperçu /Visuel.
  // "Enregistrer le brouillon" met alors à jour ce brouillon au lieu d'en créer
  // un nouveau à chaque clic — `cle` (client + type de LM) évite d'écraser ce
  // brouillon si l'utilisateur enchaîne sur une autre LM sans réinitialiser.
  const [brouillonLm, setBrouillonLm] = useState(null);

  // Rafraîchit date_jour dès que l'onglet redevient visible (évite la date figée si l'onglet reste ouvert)
  useEffect(() => {
    const refreshDateJour = () => {
      if (document.visibilityState !== "visible") return;
      const d = new Date();
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      setFormData((prev) => ({
        ...prev,
        date_jour: `${day}/${month}/${year}`,
      }));
    };
    document.addEventListener("visibilitychange", refreshDateJour);
    return () =>
      document.removeEventListener("visibilitychange", refreshDateJour);
  }, []);

  // ------------------ 👇 STATES pour suivi -------------------
  const [suiviBilanData, setSuiviBilanData] = useState([]);
  const [loadingBilan, setLoadingBilan] = useState(false);

  const [suiviCFEData, setSuiviCFEData] = useState([]);
  const [loadingCFE, setLoadingCFE] = useState(false);

  const [suiviSIData, setSuiviSIData] = useState([]);
  const [loadingSI, setLoadingSI] = useState(false);

  const [suiviTVAMensuelData, setSuiviTVAMensuelData] = useState([]);
  const [loadingTVAMensuel, setLoadingTVAMensuel] = useState(false);

  const [suiviSocialData, setSuiviSocialData] = useState([]);
  const [loadingSocial, setLoadingSocial] = useState(false);

  // Notes du Bureau (NotesBoard.jsx) — chargées une seule fois à la
  // connexion (fetchAll), pas à chaque montage du composant.
  const [bureauNotes, setBureauNotes] = useState([]);
  const [loadingBureauNotes, setLoadingBureauNotes] = useState(false);

  // Feuille de temps (FeuilleTemps.js) — même fenêtre glissante (-60j/+14j)
  // qu'avant, chargée une seule fois à la connexion.
  const [feuilleTempsEntries, setFeuilleTempsEntries] = useState([]);
  const [joursClotures, setJoursClotures] = useState([]);
  const [loadingFeuilleTemps, setLoadingFeuilleTemps] = useState(false);

  // Congés (Conges.js) — mes demandes + (si admin) la file "à valider" et
  // l'historique complet tous collaborateurs, chargées une seule fois à la
  // connexion.
  const [congesDemandes, setCongesDemandes] = useState([]);
  const [congesAValider, setCongesAValider] = useState([]);
  const [congesToutes, setCongesToutes] = useState([]);
  const [loadingConges, setLoadingConges] = useState(false);
  const [loadingCongesAValider, setLoadingCongesAValider] = useState(false);
  const [loadingCongesToutes, setLoadingCongesToutes] = useState(false);

  const [suiviTVARSIData, setSuiviTVARSIData] = useState([]);
  const [loadingTVARSI, setLoadingTVARSI] = useState(false);

  const [suiviJuridiqueData, setSuiviJuridiqueData] = useState([]);
  const [loadingJuridique, setLoadingJuridique] = useState(false);

  const [suiviIRData, setSuiviIRData] = useState([]);
  const [loadingIR, setLoadingIR] = useState(false);

  const [suiviTNSData, setSuiviTNSData] = useState([]);
  const [loadingTNS, setLoadingTNS] = useState(false);

  const [suiviComptaData, setSuiviComptaData] = useState([]);
  const [loadingCompta, setLoadingCompta] = useState(false);

  const [traitementsComptablesData, setTraitementsComptablesData] = useState(
    []
  );
  const [loadingTraitementsComptables, setLoadingTraitementsComptables] =
    useState(false);

  const [todos, setTodos] = useState([]);
  const [loadingTodos, setLoadingTodos] = useState(false);

  const [userDashboardData, setUserDashboardData] = useState(null);
  const [loadingUserDashboard, setLoadingUserDashboard] = useState(false);

  // ------------------ 👇 FETCH fonctions centralisées -------------------

  const extractTodosByUserFromHierarchy = useCallback((user) => {
    const userBlock = {
      userId: user.id,
      userKey: `${user.name} ${user.lastName}`.trim(),
      name: user.name,
      lastName: user.lastName,
      todos: user.todos || [],
    };

    let result = [userBlock];

    if (user.children && user.children.length > 0) {
      user.children.forEach((child) => {
        result = result.concat(extractTodosByUserFromHierarchy(child));
      });
    }

    return result;
  }, []);

  const fetchTodos = useCallback(async () => {
    if (!utilisateur?.id) return;

    try {
      setLoadingTodos(true);

      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/users/${utilisateur.id}/hierarchy`
      );

      const hierarchyData = await response.json();
      const allUserBlocks = extractTodosByUserFromHierarchy(hierarchyData);

      setTodos(allUserBlocks);
    } catch (error) {
      console.error("Erreur fetchTodos :", error);
    } finally {
      setLoadingTodos(false);
    }
  }, [utilisateur?.id, extractTodosByUserFromHierarchy]);

  // Mises à jour optimistes des todos : `todos` est une structure imbriquée
  // (un bloc par utilisateur avec sa liste), ces helpers appliquent le
  // changement localement pour un affichage instantané, avant la réponse API.
  const patchTodoLocal = useCallback((todoId, patch) => {
    setTodos((prev) =>
      prev.map((block) => ({
        ...block,
        todos: (block.todos || []).map((t) =>
          t.id === todoId ? { ...t, ...patch } : t
        ),
      }))
    );
  }, []);

  const removeTodoLocal = useCallback((todoId) => {
    setTodos((prev) =>
      prev.map((block) => ({
        ...block,
        todos: (block.todos || []).filter((t) => t.id !== todoId),
      }))
    );
  }, []);

  const fetchUserDashboard = useCallback(async () => {
    const ADMIN_DASHBOARD_IDS = [36, 37, 38, 39, 40, 58];
    if (!utilisateur?.id) return;
    setLoadingUserDashboard(true);
    try {
      const isAdminView = ADMIN_DASHBOARD_IDS.includes(Number(utilisateur.id));
      const url = isAdminView
        ? "https://backend-myalfa.vercel.app/api/users/hierarchy/dashboard/admin"
        : `https://backend-myalfa.vercel.app/api/users/${utilisateur.id}/hierarchy/dashboard`;
      const response = await authFetch(url);
      const data = await response.json();
      setUserDashboardData(data);
    } catch (err) {
      console.error("Erreur fetchUserDashboard :", err);
    } finally {
      setLoadingUserDashboard(false);
    }
  }, [utilisateur?.id]);

  const fetchSuiviBilan = async () => {
    // Skeleton uniquement au tout premier chargement (aucune donnée en
    // mémoire) — un refetch déclenché après édition de fiche client (voir
    // patchSuiviBilanClient) se fait en silence : l'UI a déjà été mise à
    // jour de façon optimiste, ce refetch ne fait que la re-synchroniser
    // avec le serveur sans ré-afficher le skeleton.
    if (suiviBilanData.length === 0) setLoadingBilan(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/suiviBilan${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviBilanData(data);
    } catch (err) {
      console.error("Erreur suiviBilan :", err);
    } finally {
      setLoadingBilan(false);
    }
  };

  // Patch synchrone (sans requête réseau) d'un client déjà présent dans
  // suiviBilanData — utilisé par Update.js pour refléter une édition de
  // fiche (collaborateur, régime fiscal...) instantanément dans le tableau
  // Suivi Bilan et son récap par collaborateur, sans attendre le fetchSuiviBilan
  // de fond qui suit derrière pour re-synchroniser avec le serveur.
  const patchSuiviBilanClient = (id, patch) => {
    setSuiviBilanData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) => (c.id === id ? { ...c, ...patch } : c))
        : prev
    );
  };

  // Upsert immuable d'une entrée d'année dans un tableau de suivis annuels
  // (suiviBilans[] / suiviJuridique[]...) — factorisé car utilisé deux fois
  // ci-dessous pour patcher le même changement dans deux caches différents.
  const upsertAnneeEntry = (list, annee, patch) => {
    const updated = (list || []).map((s) =>
      s.annees === annee ? { ...s, ...patch } : s
    );
    if (!updated.find((s) => s.annees === annee)) {
      updated.push({ ...patch, annees: annee });
    }
    return updated;
  };

  // Idem, mais pour le suivi annuel (suiviBilans[]) d'un client — utilisé par
  // SuiviBilan.js quand on modifie le statut/les champs de l'année affichée,
  // pour que ça survive à un démontage/remontage de l'écran (changement
  // d'onglet) au lieu de rester coincé dans le seul état local du composant.
  const patchSuiviBilanYear = (clientId, year, patch) => {
    setSuiviBilanData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) =>
            c.id === clientId
              ? { ...c, suiviBilans: upsertAnneeEntry(c.suiviBilans, year, patch) }
              : c
          )
        : prev
    );
    // suiviJuridiqueData (SuiviJuridiqueAGO.js) embarque sa propre copie de
    // suiviBilans (colonne "Statuts bilan", filtre par année, isBilanN1Client)
    // — on la patch en même temps pour qu'elle ne reste jamais périmée en
    // attendant un fetchSuiviJuridique() complet.
    setSuiviJuridiqueData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) =>
            c.id === clientId
              ? { ...c, suiviBilans: upsertAnneeEntry(c.suiviBilans, year, patch) }
              : c
          )
        : prev
    );
  };

  // Idem, pour une surcharge d'équipe cabinet par année (ClientEquipeAnnee) —
  // utilisé par Update.js (section "Assigner par année") pour que
  // collaborateursStats / le filtre par collaborateur dans Suivi Bilan
  // (resolveEquipeForYear) reflètent l'affectation instantanément, sans
  // attendre de refetch. entry = null retire la surcharge de cette année
  // (retour au comportement par défaut, ou revert après échec serveur).
  const patchSuiviBilanEquipeAnnee = (clientId, annee, entry) => {
    setSuiviBilanData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) => {
            if (c.id !== clientId) return c;
            const others = (c.equipeAnnees || []).filter(
              (e) => e.annee !== annee
            );
            return {
              ...c,
              equipeAnnees: entry ? [...others, entry] : others,
            };
          })
        : prev
    );
  };

  // Patch synchrone (sans requête réseau) d'un client déjà présent dans
  // suiviJuridiqueData — utilisé par Update.js pour refléter une édition de
  // fiche (nom, groupement, collaborateur...) instantanément dans le tableau
  // Suivi Juridique AGO, sans attendre le fetchSuiviJuridique de fond.
  const patchSuiviJuridiqueClient = (id, patch) => {
    setSuiviJuridiqueData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) => (c.id === id ? { ...c, ...patch } : c))
        : prev
    );
  };

  // Idem, mais pour le suivi annuel (suiviJuridique[]) d'un client — utilisé
  // par SuiviJuridiqueAGO.js quand on modifie le statut/les champs de l'année
  // affichée, pour que ça survive à un démontage/remontage de l'écran
  // (changement d'onglet) au lieu de rester coincé dans le seul état local
  // du composant (localSuiviJuridiqueData).
  const patchSuiviJuridiqueAnnee = (clientId, annee, patch) => {
    setSuiviJuridiqueData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) =>
            c.id === clientId
              ? {
                  ...c,
                  suiviJuridique: upsertAnneeEntry(
                    c.suiviJuridique,
                    annee,
                    patch
                  ),
                }
              : c
          )
        : prev
    );
  };

  const fetchSuiviCFE = async () => {
    setLoadingCFE(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/suiviCFE${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviCFEData(data);
    } catch (err) {
      console.error("Erreur suiviCFE :", err);
    } finally {
      setLoadingCFE(false);
    }
  };

  // Patch synchrone (sans requête réseau) d'un client déjà présent dans
  // suiviCFEData — utilisé par Update.js pour refléter une édition de fiche
  // (nom, groupement, collaborateur...) instantanément dans Suivi CFE, sans
  // attendre le fetchSuiviCFE de fond.
  const patchSuiviCFEClient = (id, patch) => {
    setSuiviCFEData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) => (c.id === id ? { ...c, ...patch } : c))
        : prev
    );
  };

  // Idem, mais pour l'unique enregistrement SuiviCFE d'un client (pas de
  // dimension année ici, contrairement à suiviBilans/suiviJuridique) —
  // utilisé par SuiviCFE.js quand on modifie le formulaire, pour que la
  // ligne du tableau reflète le changement sans attendre la réponse serveur.
  const patchSuiviCFERecord = (clientId, patch) => {
    setSuiviCFEData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) => {
            if (c.id !== clientId) return c;
            const existing = c.suiviCFE?.[0];
            const updated = existing
              ? { ...existing, ...patch }
              : { ...patch, clientId };
            return { ...c, suiviCFE: [updated] };
          })
        : prev
    );
  };

  const fetchSuiviIS = async () => {
    setLoadingSI(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/suiviIS${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviSIData(data);
    } catch (err) {
      console.error("Erreur suiviIS :", err);
    } finally {
      setLoadingSI(false);
    }
  };

  const fetchSuiviTVAMensuel = async () => {
    setLoadingTVAMensuel(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/tvaMensuel${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviTVAMensuelData(data);
    } catch (err) {
      console.error("Erreur suiviTVAMensuel :", err);
    } finally {
      setLoadingTVAMensuel(false);
    }
  };

  const fetchSuiviSocial = async () => {
    setLoadingSocial(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/suiviSocial${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviSocialData(data);
    } catch (err) {
      console.error("Erreur suiviSocial :", err);
    } finally {
      setLoadingSocial(false);
    }
  };

  // Si l'utilisateur n'a encore JAMAIS eu de note (liste vide au tout
  // premier chargement), une note d'exemple est créée automatiquement — une
  // vraie note comme les autres, modifiable/supprimable normalement, pas un
  // état factice côté front.
  const fetchBureauNotes = async () => {
    setLoadingBureauNotes(true);
    try {
      const { data } = await httpClient.get(
        "https://backend-myalfa.vercel.app/api/bureau-notes"
      );
      const fetched = data.data || [];
      if (fetched.length === 0) {
        try {
          const { data: created } = await httpClient.post(
            "https://backend-myalfa.vercel.app/api/bureau-notes",
            {
              text: "Ceci est un exemple de note — modifiez-la ou supprimez-la selon vos besoins.",
              color: "#FFE9A8",
            }
          );
          setBureauNotes([created.data]);
        } catch (err) {
          console.error("Erreur lors de la création de la note d'exemple :", err);
        }
      } else {
        setBureauNotes(fetched);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des notes :", err);
    } finally {
      setLoadingBureauNotes(false);
    }
  };

  const fetchFeuilleTemps = async () => {
    setLoadingFeuilleTemps(true);
    try {
      const now = new Date();
      const start = new Date(now.getTime() - 60 * 86400000).toISOString();
      const end = new Date(now.getTime() + 14 * 86400000).toISOString();
      const [entriesRes, closedRes] = await Promise.all([
        httpClient.get("https://backend-myalfa.vercel.app/api/saisies-temps", {
          params: { startDate: start, endDate: end },
        }),
        httpClient.get("https://backend-myalfa.vercel.app/api/jours-clotures", {
          params: { startDate: start, endDate: end },
        }),
      ]);
      setFeuilleTempsEntries(entriesRes.data.data || []);
      setJoursClotures(closedRes.data.data || []);
    } catch (err) {
      console.error("Erreur lors de la récupération de la feuille de temps :", err);
    } finally {
      setLoadingFeuilleTemps(false);
    }
  };

  const fetchCongesDemandes = async () => {
    setLoadingConges(true);
    try {
      const { data } = await httpClient.get(
        "https://backend-myalfa.vercel.app/api/conges"
      );
      setCongesDemandes(data.data || []);
    } catch (err) {
      console.error("Erreur lors de la récupération des demandes de congé :", err);
    } finally {
      setLoadingConges(false);
    }
  };

  // Réservé aux administrateurs côté backend (requireRoles("ADMINISTRATEUR")
  // sur GET /conges/a-valider) — on évite l'appel (et le 403 systématique)
  // pour tous les autres rôles.
  const fetchCongesAValider = async () => {
    if (utilisateur?.role !== "ADMINISTRATEUR" && !utilisateur?.allCabinetsAccess) return;
    setLoadingCongesAValider(true);
    try {
      const { data } = await httpClient.get(
        "https://backend-myalfa.vercel.app/api/conges/a-valider"
      );
      setCongesAValider(data.data || []);
    } catch (err) {
      console.error("Erreur lors de la récupération des demandes à valider :", err);
    } finally {
      setLoadingCongesAValider(false);
    }
  };

  // Réservé aux administrateurs côté backend (requireRoles("ADMINISTRATEUR")
  // sur GET /conges/toutes) — historique complet tous collaborateurs/statuts.
  const fetchCongesToutes = async () => {
    if (utilisateur?.role !== "ADMINISTRATEUR" && !utilisateur?.allCabinetsAccess) return;
    setLoadingCongesToutes(true);
    try {
      const { data } = await httpClient.get(
        "https://backend-myalfa.vercel.app/api/conges/toutes"
      );
      setCongesToutes(data.data || []);
    } catch (err) {
      console.error("Erreur lors de la récupération de toutes les demandes de congé :", err);
    } finally {
      setLoadingCongesToutes(false);
    }
  };

  const fetchSuiviTVARSI = async () => {
    setLoadingTVARSI(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/tvaRSI${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviTVARSIData(data);
    } catch (err) {
      console.error("Erreur suiviTVARSI :", err);
    } finally {
      setLoadingTVARSI(false);
    }
  };

  const fetchSuiviJuridique = async () => {
    setLoadingJuridique(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/suiviJuridique${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviJuridiqueData(data);
    } catch (err) {
      console.error("Erreur suiviJuridique :", err);
    } finally {
      setLoadingJuridique(false);
    }
  };

  // `silencieux: true` — resynchronisation en arrière-plan (ex. depuis
  // Annuaire.js après un changement de estTNS/tnsXxx ou autresMissions) : ne
  // touche pas loadingIR, donc si l'écran Suivi IR est déjà affiché il n'est
  // jamais reskeletonisé — seules les données se mettent à jour une fois la
  // réponse arrivée.
  const fetchSuiviIR = async ({ silencieux = false } = {}) => {
    if (!silencieux) setLoadingIR(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/suiviIR${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviIRData(data);
    } catch (err) {
      console.error("Erreur suiviIR :", err);
      throw err;
    } finally {
      if (!silencieux) setLoadingIR(false);
    }
  };

  // Même principe que fetchSuiviIR ci-dessus pour Suivi TNS — mais sans
  // filtre cabinet : comme l'Annuaire, quiconque a accès au Suivi TNS voit
  // tous les indépendants suivis (y compris ceux sans dossier rattaché).
  const fetchSuiviTNS = async ({ silencieux = false } = {}) => {
    if (!silencieux) setLoadingTNS(true);
    try {
      const { data } = await httpClient.get("https://backend-myalfa.vercel.app/api/suiviTNS");
      setSuiviTNSData(data);
    } catch (err) {
      console.error("Erreur suiviTNS :", err);
      throw err;
    } finally {
      if (!silencieux) setLoadingTNS(false);
    }
  };

  // Fusionne une ligne TNSPeriode (une année + une clé de période) dans
  // suiviTNSData sans requête réseau — utilisé par SuiviTNS.js pour que la
  // sauvegarde d'une période (overlay) mette à jour le tableau instantanément
  // au lieu de déclencher un fetchSuiviTNS() complet (qui rafficherait le
  // skeleton de toute l'interface le temps du round-trip).
  const patchSuiviTNSPeriode = (annuaireId, periodRow) => {
    setSuiviTNSData((prev) =>
      prev.map((c) => {
        if (c.id !== annuaireId) return c;
        const existingList = c.tnsPeriodes || [];
        const idx = existingList.findIndex(
          (p) => p.annee === periodRow.annee && p.periodeKey === periodRow.periodeKey
        );
        const nextList =
          idx === -1
            ? [...existingList, periodRow]
            : existingList.map((p, i) => (i === idx ? { ...p, ...periodRow } : p));
        return { ...c, tnsPeriodes: nextList };
      })
    );
  };

  // Même principe pour les missions TNS d'un indépendant (bloc "Missions TNS"
  // de la fenêtre d'une case) : `updater` reçoit la liste actuelle et renvoie
  // la nouvelle, sans requête réseau.
  const patchSuiviTNSMissions = (annuaireId, updater) => {
    setSuiviTNSData((prev) =>
      prev.map((c) => (c.id === annuaireId ? { ...c, tnsMissions: updater(c.tnsMissions || []) } : c))
    );
  };

  const fetchSuiviCompta = async () => {
    // Skeleton uniquement au tout premier chargement — un refetch déclenché
    // en arrière-plan (liaison Traitements comptables <-> Suivi Compta : voir
    // Evenement.js / ClotureStageModal.js) reste silencieux, sans réafficher
    // le skeleton.
    if (suiviComptaData.length === 0) setLoadingCompta(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/suiviCompta${
          cabinetId ? `?cabinetId=${cabinetId}` : ""
        }`
      );
      setSuiviComptaData(data);
    } catch (err) {
      console.error("Erreur suiviCompta :", err);
    } finally {
      setLoadingCompta(false);
    }
  };

  // Patch synchrone (sans requête réseau) : marque une mission Suivi Compta
  // comme faite pour un client/mois/année donnés — utilisé par Evenement.js
  // à la création d'un ticket "Arrivée pièces comptables" pour que la
  // mission "Arrive PC" apparaisse instantanément dans Suivi Compta, sans
  // attendre le fetchSuiviCompta de fond qui suit derrière pour resynchroniser
  // avec les vraies données du formulaire (voir comptaAutoLink.js côté
  // backend). Si le client n'est pas encore présent dans suiviComptaData,
  // ne fait rien (le fetchSuiviCompta de fond s'en chargera).
  const patchSuiviComptaTacheStatut = (clientId, annee, mois, type) => {
    setSuiviComptaData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) => {
            if (c.id !== clientId) return c;
            const compta =
              c.Compta && c.Compta.length > 0 ? c.Compta[0] : { id: null, mois: [] };
            const moisExistant = compta.mois.find(
              (m) => m.numero === mois && m.annee === annee
            );
            const nouveauMois = moisExistant
              ? {
                  ...moisExistant,
                  taches: moisExistant.taches?.some((t) => t.type === type)
                    ? moisExistant.taches.map((t) =>
                        t.type === type ? { ...t, statut: true } : t
                      )
                    : [...(moisExistant.taches || []), { id: null, type, statut: true }],
                }
              : { id: null, numero: mois, annee, taches: [{ id: null, type, statut: true }] };
            const autresMois = compta.mois.filter(
              (m) => !(m.numero === mois && m.annee === annee)
            );
            return { ...c, Compta: [{ ...compta, mois: [...autresMois, nouveauMois] }] };
          })
        : prev
    );
  };

  // Patch synchrone (sans requête réseau) d'un client déjà présent dans
  // suiviComptaData — utilisé par Update.js pour refléter une édition de
  // fiche (nom, groupement, collaborateur...) instantanément dans Suivi
  // Compta, sans attendre le fetchSuiviCompta de fond.
  const patchSuiviComptaClient = (id, patch) => {
    setSuiviComptaData((prev) =>
      Array.isArray(prev)
        ? prev.map((c) => (c.id === id ? { ...c, ...patch } : c))
        : prev
    );
  };

  const fetchTraitementsComptables = async () => {
    // Skeleton uniquement au tout premier chargement — un refetch déclenché
    // après création d'un évènement "Arrivée pièces comptables" (voir
    // Evenement.js) se fait en silence, sans réafficher le skeleton.
    if (traitementsComptablesData.length === 0)
      setLoadingTraitementsComptables(true);
    try {
      const { data } = await httpClient.get(
        "https://backend-myalfa.vercel.app/api/traitements-comptables/tickets"
      );
      setTraitementsComptablesData(data.data || []);
    } catch (err) {
      console.error("Erreur traitements comptables :", err);
    } finally {
      setLoadingTraitementsComptables(false);
    }
  };

  const [evenements, setEvenements] = useState([]);
  const [loadingEvent, setLoadingEvent] = useState(true);

  const fetchEvenements = async () => {
    try {
      const response = await authFetch(
        "https://backend-myalfa.vercel.app/api/evenements"
      );
      const data = await response.json();
      // Sur une erreur serveur, l'API répond un objet ({ error: ... }) au
      // lieu d'un tableau — sans cette garde, evenements.forEach(...) plante
      // toute l'appli partout où evenements est utilisé (ex. AllEvenement.js).
      if (!response.ok || !Array.isArray(data)) {
        console.error("Réponse inattendue de /api/evenements :", data);
        return;
      }
      setEvenements(data);
    } catch (error) {
      console.error("Erreur lors du chargement des événements :", error);
    } finally {
      setLoadingEvent(false);
    }
  };

  // Aperçu Google Calendar (statut de connexion + prochains événements sur
  // 60 jours glissants) — chargé une fois ici, globalement, à la connexion
  // (voir fetchAll plus bas), plutôt que dans chaque écran qui en a besoin
  // (Bureau > "Mes rendez-vous à venir"). Indépendant de fetchAll/isLoading
  // pour ne pas ralentir tout le reste si l'appel Google est lent ; Planning.js
  // garde son propre fetch d'événements (fenêtre = semaine affichée, besoin
  // différent), seul le statut de connexion pourrait diverger un instant
  // avant que Planning.js ne rafraîchisse le sien à son montage.
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [googleCalendarUpcomingEvents, setGoogleCalendarUpcomingEvents] = useState([]);
  const [loadingGoogleCalendarOverview, setLoadingGoogleCalendarOverview] = useState(true);

  const fetchGoogleCalendarOverview = async () => {
    setLoadingGoogleCalendarOverview(true);
    try {
      const { data: status } = await httpClient.get("/api/users/me/google-calendar/status");
      setGoogleCalendarConnected(!!status.connected);

      if (!status.connected) {
        setGoogleCalendarUpcomingEvents([]);
        return;
      }

      const now = new Date();
      const in60Days = new Date(now.getTime() + 60 * 86400000);
      const { data } = await httpClient.get("/api/users/me/google-calendar/events", {
        params: { timeMin: now.toISOString(), timeMax: in60Days.toISOString() },
      });
      setGoogleCalendarUpcomingEvents(data.events || []);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'aperçu Google Calendar :", error);
    } finally {
      setLoadingGoogleCalendarOverview(false);
    }
  };

  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLead] = useState(true);
  const leadsLoadedOnceRef = useRef(false);

  const [annuaireData, setAnnuaireData] = useState([]);
  const [loadingAnnuaire, setLoadingAnnuaire] = useState(false);

  const [mandats, setMandats] = useState([]);
  const [loadingMandats, setLoadingMandats] = useState(false);

  // Référentiel KYC/LAB (Paramètres KYC/LAB : formes, activités NAF, pays,
  // missions, questions) chargé au démarrage avec le reste : les recherches
  // de code activité / pays (fiche client, module KYC) sont instantanées.
  // { listes: { formes, activites, pays, missions, questions }, version }
  const [kycReferentiel, setKycReferentiel] = useState(null);
  const fetchKycReferentiel = async () => {
    try {
      const { data } = await httpClient.get("https://backend-myalfa.vercel.app/api/gi/kyc-referentiel");
      setKycReferentiel({ listes: data.listes, version: data.version });
      return data;
    } catch (err) {
      console.error("Erreur référentiel KYC/LAB :", err);
      return null;
    }
  };
  // Catalogue de diligences (Paramètres KYC/LAB › Diligences) chargé au
  // démarrage comme le référentiel : l'onglet s'affiche sans attente.
  // Réponse de GET /api/gi/kyc-diligences (diligences, règles, champs, tags,
  // droits) ; mis à jour par l'écran après chaque modification.
  const [kycDiligences, setKycDiligences] = useState(null);
  const fetchKycDiligences = async () => {
    try {
      const { data } = await httpClient.get("https://backend-myalfa.vercel.app/api/gi/kyc-diligences");
      setKycDiligences(data);
      return data;
    } catch (err) {
      console.error("Erreur catalogue de diligences KYC/LAB :", err);
      return null;
    }
  };
  // Remplace une entrée modifiée dans les Paramètres KYC/LAB (sans recharger).
  const patchKycReferentielEntree = (entree, version) => {
    setKycReferentiel((prev) =>
      prev
        ? {
            version: version ?? prev.version,
            listes: {
              ...prev.listes,
              [entree.liste]: (prev.listes[entree.liste] || []).map((e) => (e.id === entree.id ? entree : e)),
            },
          }
        : prev
    );
  };

  const fetchLeads = async () => {
    // Le skeleton de chargement ne s'affiche qu'au tout premier fetch ;
    // les rafraîchissements suivants (après une modification en arrière-plan) restent silencieux.
    const isFirstLoad = !leadsLoadedOnceRef.current;
    if (isFirstLoad) setLoadingLead(true);
    try {
      // Pas de filtre cabinet sur les leads (demande Jonathan, 21/09/2026) —
      // contrairement aux autres modules (Portefeuille, Planning...), tout
      // collaborateur doit voir tous les leads, quel que soit son cabinet.
      const response = await authFetch(
        "https://backend-myalfa.vercel.app/api/formleads"
      );
      const result = await response.json();
      setLeads(result.data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des leads :", error);
    } finally {
      leadsLoadedOnceRef.current = true;
      if (isFirstLoad) setLoadingLead(false);
    }
  };

  const fetchMandats = async () => {
    setLoadingMandats(true);
    try {
      const { data } = await httpClient.get(
        "https://backend-myalfa.vercel.app/api/mandats/all"
      );
      setMandats(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur fetchMandats :", err);
    } finally {
      setLoadingMandats(false);
    }
  };

  const fetchAnnuaire = async () => {
    // Skeleton uniquement au tout premier chargement — un refetch de
    // réconciliation déclenché après création/édition (déjà appliquée de
    // façon optimiste via addAnnuaireContact/updateAnnuaireContact) se fait
    // en silence, sans réafficher les skeletons de la grille Annuaire.js.
    if (annuaireData.length === 0) setLoadingAnnuaire(true);
    try {
      const { data } = await httpClient.get(
        "https://backend-myalfa.vercel.app/api/gi/annuaire"
      );
      setAnnuaireData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur annuaire GI :", err);
    } finally {
      setLoadingAnnuaire(false);
    }
  };

  const updateAnnuaireContact = (id, updatedFields) => {
    setAnnuaireData((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...updatedFields } : c))
    );
  };

  // Ajoute un nouveau contact à annuaireData sans requête réseau — utilisé
  // par Annuaire.js après une création réussie (NouveauContactForm a déjà
  // fait le POST et renvoie l'objet complet créé), pour qu'il apparaisse
  // instantanément dans la liste et devienne immédiatement recherchable
  // ailleurs (groupement dans Update.js) sans attendre un fetchAnnuaire().
  const addAnnuaireContact = (contact) => {
    setAnnuaireData((prev) => [contact, ...prev]);
  };

  const mergeAnnuaireContacts = async (keepId, mergeIds, data) => {
    const { data: merged } = await httpClient.post(
      "https://backend-myalfa.vercel.app/api/gi/annuaire/merge",
      { keepId, mergeIds, data }
    );
    setAnnuaireData((prev) =>
      prev
        .filter((c) => !mergeIds.includes(c.id))
        .map((c) => (c.id === keepId ? merged : c))
    );
    return merged;
  };

  // EtatGlobalProvider.jsx (ajout)
  const [juridiquePonctuelleData, setJuridiquePonctuelleData] = useState([]);
  const [loadingJuridiquePonctuelle, setLoadingJuridiquePonctuelle] =
    useState(false);

  const fetchJuridiquePonctuelle = async () => {
    setLoadingJuridiquePonctuelle(true);
    try {
      const cabinetId = getCabinetIdFilter(utilisateur);
      const qs = cabinetId ? `?cabinetId=${cabinetId}` : "";

      let droitsocData,
        modifsctData,
        creationsctData,
        RadiationData,
        autresMissionData;

      // Endpoint consolidé : 1 seule requête au lieu de 5. Repli automatique
      // sur les 5 GET historiques tant que le backend n'est pas déployé.
      try {
        const aggRes = await authFetch(
          `https://backend-myalfa.vercel.app/api/juridique-ponctuelle${qs}`
        );
        if (!aggRes.ok) throw new Error(`HTTP ${aggRes.status}`);
        const agg = await aggRes.json();
        droitsocData = agg.droitsoc;
        modifsctData = agg.modifsct;
        creationsctData = agg.creationsct;
        RadiationData = agg.radiations;
        autresMissionData = agg.autresMissions;
      } catch (aggError) {
        console.warn(
          "Endpoint juridique-ponctuelle indisponible, repli sur les 5 requêtes :",
          aggError
        );
        const [
          droitsocRes,
          modifsctRes,
          creationsctRes,
          RadiationRes,
          autresMissionRes,
        ] = await Promise.all([
          authFetch(`https://backend-myalfa.vercel.app/api/droitsoc${qs}`),
          authFetch(`https://backend-myalfa.vercel.app/api/modifsct${qs}`),
          authFetch(`https://backend-myalfa.vercel.app/api/creation-sct${qs}`),
          authFetch(`https://backend-myalfa.vercel.app/api/Radiation${qs}`),
          authFetch(`https://backend-myalfa.vercel.app/api/autresMission${qs}`),
        ]);

        [
          droitsocData,
          modifsctData,
          creationsctData,
          RadiationData,
          autresMissionData,
        ] = await Promise.all([
          droitsocRes.json(),
          modifsctRes.json(),
          creationsctRes.json(),
          RadiationRes.json(),
          autresMissionRes.json(),
        ]);
      }

      const droitsocFormatted = droitsocData.map((item) => ({
        ...item,
        source: "droitsoc",
        missionLabel: "Droit Sociaux",
        descriptif: item.client?.formeSociale || "-",
      }));

      const modifsctFormatted = modifsctData.map((item) => {
        const details = [];
        if (item.activeForme) details.push("Forme Sociale");
        if (item.activeSiege) details.push("Siège");
        if (item.activeDateCloture) details.push("Date Clôture");
        if (item.activeDenomination) details.push("Dénomination");
        if (item.activeObjetSocial) details.push("Objet Social");
        if (item.activeCapital) details.push("Capital");
        if (item.activeDirigeant) details.push("Dirigeant");

        return {
          ...item,
          source: "modifsct",
          missionLabel: "Modification de Société",
          descriptif: details.join(" / ") || "-",
        };
      });

      const creationsctFormatted = creationsctData.map((item) => ({
        ...item,
        source: "creationsct",
        missionLabel: "Immatriculation",
        descriptif: [item.formeSociale, item.regimeFiscal]
          .filter(Boolean)
          .join(" / "),
      }));

      const radiationFormatted = RadiationData.map((item) => ({
        ...item,
        source: "radiation",
        missionLabel: "Radiation",
        descriptif: "Radiation",
      }));

      const autresMissionFormatted = autresMissionData.map((item) => ({
        ...item,
        source: "autresMission",
        missionLabel: "Autres",
        descriptif: "-",
      }));

      setJuridiquePonctuelleData([
        ...droitsocFormatted,
        ...modifsctFormatted,
        ...creationsctFormatted,
        ...radiationFormatted,
        ...autresMissionFormatted,
      ]);
    } catch (error) {
      console.error("Erreur fetch JuridiquePonctuelle :", error);
    } finally {
      setLoadingJuridiquePonctuelle(false);
    }
  };

  // Patch synchrone (sans requête réseau) du client embarqué dans chaque
  // item de juridiquePonctuelleData (droitsoc/modifsct/creationsct/
  // radiation/autresMission) — utilisé par Update.js pour refléter une
  // édition de fiche (nom, groupement, collaborateur...) instantanément
  // dans JuridiquePonctuelle.js, sans attendre le fetchJuridiquePonctuelle
  // de fond. Contrairement à suiviBilanData/suiviJuridiqueData (un tableau
  // de clients), ici c'est un tableau plat d'items qui portent chacun un
  // client — on patche donc `item.client` partout où l'id correspond.
  const patchJuridiquePonctuelleClient = (clientId, patch) => {
    setJuridiquePonctuelleData((prev) =>
      Array.isArray(prev)
        ? prev.map((item) =>
            item.client?.id === clientId
              ? { ...item, client: { ...item.client, ...patch } }
              : item
          )
        : prev
    );
  };

  // ------------------ 👇 FETCH fonctions centralisées -------------------
  const fetchAll = useCallback(async () => {
    try {
      setIsLoading(true);

      await Promise.all([
        fetchTodos(),
        fetchUserDashboard(),
        fetchSuiviSocial(),
        fetchBureauNotes(),
        fetchFeuilleTemps(),
        fetchCongesDemandes(),
        fetchCongesAValider(),
        fetchCongesToutes(),
        fetchDocumentsLM(),
        fetchClietns(),
        fetchUsers(),
        fetchSuiviBilan(),
        fetchSuiviCFE(),
        fetchSuiviIS(),
        fetchSuiviTVAMensuel(),
        fetchSuiviTVARSI(),
        fetchSuiviJuridique(),
        fetchSuiviIR(),
        fetchSuiviTNS(),
        fetchSuiviCompta(),
        fetchTraitementsComptables(),
        fetchJuridiquePonctuelle(),
        fetchEvenements(),
        fetchLeads(),
        fetchAnnuaire(),
        fetchMandats(),
        fetchKycReferentiel(),
        fetchKycDiligences(),
      ]);
    } catch (error) {
      console.error("Erreur dans fetchAll:", error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTodos, fetchUserDashboard]);

  // Fire fetchAll only when a user actually logs in (userId changes)
  const prevUserIdRef = useRef(null);
  useEffect(() => {
    const currentId = utilisateur?.id ?? null;
    if (!currentId || currentId === prevUserIdRef.current) return;
    prevUserIdRef.current = currentId;
    fetchAll();
    // Volontairement hors du Promise.all de fetchAll (isLoading) : un appel
    // Google lent ne doit pas retarder tous les autres squelettes de l'app.
    fetchGoogleCalendarOverview();
  }, [utilisateur, fetchAll]);

  // Compteur de messages Discussion non lus — état partagé (Bureau.jsx pour
  // le badge du raccourci, Home.js pour le badge de la cloche du topbar),
  // pour ne poller /api/discussion/unread-count qu'une seule fois au lieu
  // d'un fetch dupliqué par écran. fetchDiscussionUnreadCount sert aussi de
  // refresh forcé (ex. après un "marquer comme lu" dans le drawer de
  // notifications, sans attendre le prochain tick de 30s).
  const [discussionUnreadCount, setDiscussionUnreadCount] = useState(0);

  const fetchDiscussionUnreadCount = useCallback(() => {
    if (!utilisateur?.id) return;
    return httpClient
      .get("/api/discussion/unread-count")
      .then(({ data }) => setDiscussionUnreadCount(data.data.count))
      .catch((err) => console.error("Erreur compteur Discussion :", err));
  }, [utilisateur?.id]);

  useEffect(() => {
    fetchDiscussionUnreadCount();
    const interval = setInterval(fetchDiscussionUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchDiscussionUnreadCount]);

  // Periodic todos refresh — independent of auth, but guarded by token
  useEffect(() => {
    let isFetching = false;
    const interval = setInterval(async () => {
      if (!localStorage.getItem("token")) return;
      if (isFetching) return;
      try {
        isFetching = true;
        await fetchTodos();
        console.log(
          "~~~~~~~~~~~~~Données mises à jour via fetchTodos~~~~~~~~~~~~~"
        );
      } finally {
        isFetching = false;
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchTodos]);

  return (
    <EtatGlobalContext.Provider
      value={{
        utilisateur,
        setUtilisateur,
        impersonatorSession,
        impersonateUser,
        stopImpersonation,
        formData,
        setFormData,
        documents,
        setDocuments,
        users,
        setUsers,
        isLoading,
        setIsLoading,
        loadingDocumentsLM,
        updateFormData,
        rows,
        setRows: updateRows,
        fetchDocumentsLM,
        nombreLignesTableau,
        setNombreLignesTableau,
        nombreLignesTexteLibre,
        setNombreLignesTexteLibre,
        afficheLM,
        setAfficheLM,
        cabinetRequisVisible,
        setCabinetRequisVisible,
        leadIdGlobal,
        setLeadIdGlobal,
        clientIdGlobal,
        setClientIdGlobal,
        brouillonLm,
        setBrouillonLm,
        discussionUnreadCount,
        fetchDiscussionUnreadCount,
        // 👇 Ajout suivi dans le provider
        todos,
        setTodos,
        loadingTodos,
        fetchTodos,
        patchTodoLocal,
        removeTodoLocal,
        suiviBilanData,
        loadingBilan,
        fetchSuiviBilan,
        patchSuiviBilanClient,
        patchSuiviBilanYear,
        patchSuiviBilanEquipeAnnee,
        suiviCFEData,
        loadingCFE,
        fetchSuiviCFE,
        patchSuiviCFEClient,
        patchSuiviCFERecord,
        suiviSIData,
        loadingSI,
        fetchSuiviIS,
        suiviTVAMensuelData,
        loadingTVAMensuel,
        fetchSuiviTVAMensuel,
        suiviSocialData,
        loadingSocial,
        fetchSuiviSocial,
        bureauNotes,
        setBureauNotes,
        loadingBureauNotes,
        fetchBureauNotes,
        feuilleTempsEntries,
        setFeuilleTempsEntries,
        joursClotures,
        loadingFeuilleTemps,
        fetchFeuilleTemps,
        congesDemandes,
        setCongesDemandes,
        congesAValider,
        setCongesAValider,
        congesToutes,
        setCongesToutes,
        loadingConges,
        loadingCongesAValider,
        loadingCongesToutes,
        fetchCongesDemandes,
        fetchCongesAValider,
        fetchCongesToutes,
        suiviTVARSIData,
        loadingTVARSI,
        fetchSuiviTVARSI,
        suiviJuridiqueData,
        loadingJuridique,
        fetchSuiviJuridique,
        patchSuiviJuridiqueClient,
        patchSuiviJuridiqueAnnee,
        suiviIRData,
        loadingIR,
        fetchSuiviIR,
        suiviTNSData,
        loadingTNS,
        fetchSuiviTNS,
        patchSuiviTNSPeriode,
        patchSuiviTNSMissions,
        suiviComptaData,
        loadingCompta,
        fetchSuiviCompta,
        patchSuiviComptaClient,
        patchSuiviComptaTacheStatut,
        traitementsComptablesData,
        setTraitementsComptablesData,
        loadingTraitementsComptables,
        fetchTraitementsComptables,
        //
        juridiquePonctuelleData,
        setJuridiquePonctuelleData,
        loadingJuridiquePonctuelle,
        fetchJuridiquePonctuelle,
        patchJuridiquePonctuelleClient,
        //
        evenements,
        loadingEvent,
        //
        googleCalendarConnected,
        googleCalendarUpcomingEvents,
        loadingGoogleCalendarOverview,
        fetchGoogleCalendarOverview,
        //
        leads,
        setLeads,
        loadingLeads,
        fetchLeads,
        //
        annuaireData,
        loadingAnnuaire,
        fetchAnnuaire,
        updateAnnuaireContact,
        addAnnuaireContact,
        mergeAnnuaireContacts,
        //
        mandats,
        setMandats,
        loadingMandats,
        fetchMandats,
        //
        kycReferentiel,
        fetchKycReferentiel,
        patchKycReferentielEntree,
        kycDiligences,
        setKycDiligences,
        fetchKycDiligences,
        //
        fetchAll,
        //
        userDashboardData,
        setUserDashboardData,
        loadingUserDashboard,
        fetchUserDashboard,
        // ---------------------
        showUpdateNotification,
        closeUpdateNotification,
        resetUpdateNotification,
        CURRENT_UPDATE_VERSION,
      }}
    >
      {children}
    </EtatGlobalContext.Provider>
  );
};

// Hook personnalisé pour accéder à l'état global
export const useEtatGlobal = () => {
  return useContext(EtatGlobalContext);
};
